import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import AdminLayout from '@/components/feature/AdminLayout';
import { ErrorState } from '@/components/feature/AsyncState';
import PendingAlerts from '@/components/feature/PendingAlerts';
import { useFinances } from '@/hooks/useFinances';
import { useOrders } from '@/hooks/useOrders';
import { useSales } from '@/hooks/useSales';
import { downloadCsv } from '@/lib/csv';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const COLORS = ['oklch(var(--accent-500))', 'oklch(var(--primary-500))', 'oklch(var(--secondary-500))', '#14b8a6'];

export default function DashboardPage() {
  const location = useLocation();
  const { data: fin, error, refetch } = useFinances();
  const { data: orders } = useOrders();
  const { data: sales } = useSales();
  const accessDenied = (location.state as { accessDenied?: boolean } | null)?.accessDenied === true;

  const pendingOrders = orders.filter((o) =>
    ['received', 'pending_payment', 'payment_initiated', 'payment_confirmed', 'preparing'].includes(o.status),
  ).length;
  const deliveredOrders = orders.filter((o) => o.status === 'delivered').length;

  const avgTicket = fin.saleCount > 0 ? Math.round(fin.monthSalesTotal / fin.saleCount) : 0;

  const channelData = useMemo(() => {
    const map: Record<string, number> = {};
    sales.forEach((s) => {
      const key = s.channel === 'ecommerce' ? 'E-commerce' : s.channel === 'direct' ? 'Directa' : s.channel || 'Otro';
      map[key] = (map[key] || 0) + s.total;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [sales]);

  const donationRateLabel = `${fin.donationPercent}%`;
  const commissionRateLabel = `${fin.commissionPercent}%`;

  function exportDashboard() {
    downloadCsv(
      'dashboard-pedidos',
      ['Pedido', 'Cliente', 'Canal', 'Total', 'Estado', 'Fecha'],
      orders.map((o) => [
        o.orderNumber,
        o.customerName,
        o.channel,
        o.total,
        o.status,
        o.createdAt ? new Date(o.createdAt).toLocaleDateString('es-CO') : '',
      ]),
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-6 max-w-[1400px] mx-auto">
        {error && <ErrorState message={error} onRetry={refetch} />}
        {accessDenied && (
          <div className="flex items-start gap-3 bg-red-50 text-red-700 border border-red-200 rounded-xl px-4 py-3">
            <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
            <div>
              <p className="text-sm font-semibold">Acceso Denegado</p>
              <p className="text-xs mt-0.5">No tienes permisos para acceder a esa sección.</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Dashboard</h1>
            <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
              <span>Resumen ejecutivo consolidado</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                Supabase
              </span>
            </p>
          </div>
          <button
            onClick={exportDashboard}
            className="text-sm px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
          >
            <i className="ri-download-line mr-1.5"></i>Exportar
          </button>
        </div>

        {/* Alertas de pagos / donaciones pendientes */}
        <PendingAlerts />

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <KpiCard label="Ventas Hoy" value={`$${fin.todaySalesTotal.toLocaleString()}`} icon="ri-sun-line" color="bg-accent-500" />
          <KpiCard label="Ventas del Mes" value={`$${fin.monthSalesTotal.toLocaleString()}`} icon="ri-calendar-line" color="bg-primary-500" />
          <KpiCard label="Comisiones pagadas" value={`$${fin.totalCommissions.toLocaleString()}`} icon="ri-percent-line" color="bg-emerald-600" />
          <KpiCard label={`Donaciones (${donationRateLabel})`} value={`$${fin.totalDonations.toLocaleString()}`} icon="ri-heart-line" color="bg-pink-500" />
          <KpiCard label="Stock Total" value={`${fin.totalStock} pares`} icon="ri-stack-line" color="bg-amber-500" />
          <KpiCard label="Valor Inventario" value={`$${Math.round(fin.inventoryValue / 1000000 * 10) / 10}M`} icon="ri-funds-line" color="bg-foreground-700" />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <KpiCard label="Costo Inventario" value={`$${Math.round(fin.inventoryCost / 1000000 * 10) / 10}M`} icon="ri-money-dollar-circle-line" color="bg-secondary-500" />
          <KpiCard label="Ticket Promedio" value={`$${avgTicket.toLocaleString()}`} icon="ri-ticket-line" color="bg-teal-500" />
          <KpiCard label="Pedidos Pend." value={String(pendingOrders)} icon="ri-hourglass-line" color="bg-amber-600" />
          <KpiCard label="Pedidos Entregados" value={String(deliveredOrders)} icon="ri-check-double-line" color="bg-emerald-500" />
          <KpiCard label="Fondo Donaciones" value={`$${fin.accumulatedDonations.toLocaleString()}`} icon="ri-heart-pulse-line" color="bg-rose-500" />
          <KpiCard label="Ventas del Mes" value={String(fin.saleCount)} icon="ri-shopping-bag-3-line" color="bg-accent-600" />
        </div>

        {/* Capital y Capital Neto */}
        <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
                <i className="ri-wallet-3-line text-accent-600"></i>Capital
              </h3>
              <p className="text-xs text-foreground-500 mt-1 max-w-2xl leading-relaxed">
                <strong>Capital</strong> = inventario a costo + ingreso bruto. Luego se descuenta el costo de lo vendido,
                gastos, comisiones pagadas, envíos, marketing, donaciones y devoluciones reembolsadas. La proyección a venta es solo informativa y no suma.
              </p>
            </div>
            <div className="text-left sm:text-right flex-shrink-0">
              <p className="text-xs text-foreground-500">Capital Neto</p>
              <p className={`text-2xl font-bold ${fin.capitalNeto >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {fin.capitalNeto < 0 ? '-' : ''}${Math.abs(Math.round(fin.capitalNeto)).toLocaleString()}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            <CapitalItem label="Inventario a costo" value={fin.inventoryCost} icon="ri-archive-line" tone="primary" />
            <CapitalItem label="Proyección a venta (informativo)" value={fin.inventoryValue} icon="ri-line-chart-line" tone="muted" note="No suma al capital" />
            <CapitalItem label="Ingreso bruto" value={fin.grossSalesTotal} icon="ri-money-dollar-circle-line" tone="accent" note={`Invertido: $${Math.round(fin.cogsTotal).toLocaleString()}`} />
            <CapitalItem label="Ingreso neto" value={fin.grossSalesTotal - fin.cogsTotal} icon="ri-funds-line" tone="accent" note="Bruto − costo de lo vendido" />
            <CapitalItem label="Gastos operativos" value={-fin.totalExpenses} icon="ri-receipt-line" tone="negative" note="Se resta" />
            <CapitalItem label="Comisiones pagadas" value={-fin.totalCommissions} icon="ri-percent-line" tone="negative" note="Se resta (solo pagadas)" />
            <CapitalItem label="Costo de envíos" value={-fin.shippingOperationalCost} icon="ri-truck-line" tone="negative" note="Se resta (solo envíos facturados)" />
            <CapitalItem label="Marketing (consolidado)" value={-fin.marketingInvestment} icon="ri-megaphone-line" tone="negative" note="Se resta" />
            <CapitalItem label="Donaciones" value={-fin.totalDonations} icon="ri-heart-line" tone="negative" note="Se resta" />
            <CapitalItem label="Devoluciones reembolsadas" value={-fin.refundedReturns} icon="ri-arrow-go-back-line" tone="negative" note="Se resta" />
            <CapitalItem label="Comisión contra entrega" value={-fin.codFeesCost} icon="ri-cash-line" tone="negative" note="Se resta (recaudo COD)" />
            <CapitalItem label="Recargo COD cobrado" value={fin.codSurchargeCollected} icon="ri-hand-coin-line" tone="muted" note="Informativo: ya va en el ingreso bruto" />
          </div>
        </div>

        {/* Aviso: comisiones pendientes por pagar (informativo, no afecta el Capital) */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
              <i className="ri-error-warning-line"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-900">Comisiones pendientes por pagar</p>
              <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
                Monto que aún debes a los vendedores. Es un aviso informativo: <strong>no</strong> afecta el Capital Neto (el
                capital solo resta las comisiones ya pagadas).
              </p>
            </div>
          </div>
          <p className="text-2xl font-bold text-amber-700 whitespace-nowrap sm:text-right">
            ${fin.pendingCommissions.toLocaleString()}
          </p>
        </div>

        {/* Desglose de envíos: cobrado vs. pagado (informativo) */}
        <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
                <i className="ri-truck-line text-accent-600"></i>Envíos: cobrado vs. pagado
              </h3>
              <p className="text-xs text-foreground-500 mt-1 max-w-2xl leading-relaxed">
                Compara el flete que <strong>cobras</strong> al cliente con el que <strong>pagas</strong> a la transportadora. El flete de las
                babuchas premium va incluido en el precio, así que se paga pero no se cobra. Informativo: no afecta el Capital Neto.
              </p>
            </div>
            <div className="text-left sm:text-right flex-shrink-0">
              <p className="text-xs text-foreground-500">Margen del flete</p>
              <p className={`text-2xl font-bold ${fin.shippingMargin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {fin.shippingMargin < 0 ? '-' : ''}${Math.abs(Math.round(fin.shippingMargin)).toLocaleString()}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <CapitalItem label="Envíos cobrados al cliente" value={fin.shippingCollected} icon="ri-arrow-down-line" tone="primary" note="Lo que paga el cliente" />
            <CapitalItem
              label="Costo de envíos pagado"
              value={-fin.shippingPaidCost}
              icon="ri-truck-line"
              tone="negative"
              note={`Incluye $${Math.round(fin.premiumShippingAbsorbed).toLocaleString()} de flete premium absorbido`}
            />
            <CapitalItem
              label="Margen del flete"
              value={fin.shippingMargin}
              icon="ri-scales-3-line"
              tone={fin.shippingMargin >= 0 ? 'accent' : 'negative'}
              note={fin.shippingMargin >= 0 ? 'El flete te deja margen' : 'El flete te cuesta'}
            />
          </div>
        </div>

        {/* Reglas de negocio PCP */}
        <div className="bg-gradient-to-r from-accent-50 to-primary-50 rounded-2xl border border-background-200 p-5 md:p-6">
          <h3 className="text-sm font-semibold text-foreground-900 mb-4 flex items-center gap-2">
            <i className="ri-heart-pulse-line text-accent-600"></i>Reglas de negocio PCP
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl p-4 border border-background-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
                    <i className="ri-heart-line text-pink-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">Donaciones {donationRateLabel}</p>
                    <p className="text-xs text-foreground-500">Fondo de impacto social para bienestar animal</p>
                  </div>
                </div>
                <p className="text-lg font-bold text-pink-600">${fin.totalDonations.toLocaleString()}</p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-background-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <i className="ri-percent-line text-emerald-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">Comisiones {commissionRateLabel}</p>
                    <p className="text-xs text-foreground-500">Sobre la ganancia, ya pagadas a vendedores</p>
                  </div>
                </div>
                <p className="text-lg font-bold text-emerald-600">${fin.totalCommissions.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-background-200 p-5 lg:col-span-1">
            <h3 className="text-sm font-semibold text-foreground-900 mb-4">Ventas por Canal</h3>
            {channelData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={channelData} cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3} dataKey="value">
                    {channelData.map((_, idx) => (
                      <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => `$${value.toLocaleString()}`} />
                  <Legend formatter={(value: string) => <span className="text-xs text-foreground-700">{value}</span>} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-foreground-400 text-sm">
                Sin ventas registradas
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-background-200 p-5 lg:col-span-2">
            <h3 className="text-sm font-semibold text-foreground-900 mb-4">Indicadores de Propósito</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="rounded-xl bg-accent-50 p-4 text-center">
                <p className="text-3xl font-bold text-accent-600">{fin.donationPercent}%</p>
                <p className="text-xs text-foreground-600 mt-1">de cada venta</p>
                <p className="text-xs text-foreground-500">para fundaciones</p>
              </div>
              <div className="rounded-xl bg-emerald-50 p-4 text-center">
                <p className="text-3xl font-bold text-emerald-600">{fin.commissionPercent}%</p>
                <p className="text-xs text-foreground-600 mt-1">comisión por venta</p>
                <p className="text-xs text-foreground-500">para agentes comerciales</p>
              </div>
              <div className="rounded-xl bg-pink-50 p-4 text-center">
                <p className="text-3xl font-bold text-pink-600">${fin.accumulatedDonations.toLocaleString()}</p>
                <p className="text-xs text-foreground-600 mt-1">fondo acumulado</p>
                <p className="text-xs text-foreground-500">bienestar animal</p>
              </div>
            </div>
          </div>
        </div>

        {/* Pedidos Recientes */}
        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="px-5 py-4 flex items-center justify-between border-b border-background-100">
            <h3 className="text-sm font-semibold text-foreground-900">Pedidos Recientes</h3>
            <Link to="/admin/pedidos" className="text-xs text-accent-600 hover:text-accent-700 font-medium whitespace-nowrap">
              Ver todos →
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 text-left">
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Pedido</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Cliente</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Canal</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Total</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Estado</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {orders.slice(0, 6).map((order) => {
                  const statusMap: Record<string, { label: string; color: string; bg: string }> = {
                    received: { label: 'Recibido', color: 'text-blue-600', bg: 'bg-blue-50' },
                    pending_payment: { label: 'Pend. Pago', color: 'text-amber-600', bg: 'bg-amber-50' },
                    payment_initiated: { label: 'Pago Iniciado', color: 'text-amber-600', bg: 'bg-amber-50' },
                    payment_confirmed: { label: 'Pago Confirmado', color: 'text-emerald-600', bg: 'bg-emerald-50' },
                    preparing: { label: 'Preparando', color: 'text-indigo-600', bg: 'bg-indigo-50' },
                    shipped: { label: 'Despachado', color: 'text-cyan-600', bg: 'bg-cyan-50' },
                    delivered: { label: 'Entregado', color: 'text-emerald-700', bg: 'bg-emerald-50' },
                    cancelled: { label: 'Cancelado', color: 'text-red-600', bg: 'bg-red-50' },
                  };
                  const s = statusMap[order.status] || { label: order.status, color: 'text-foreground-500', bg: 'bg-background-100' };
                  return (
                    <tr key={order.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                      <td className="px-5 py-3">
                        <Link to="/admin/pedidos" className="text-accent-600 font-medium hover:underline whitespace-nowrap">
                          {order.orderNumber}
                        </Link>
                      </td>
                      <td className="px-5 py-3 text-foreground-700 whitespace-nowrap">{order.customerName || '—'}</td>
                      <td className="px-5 py-3">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 capitalize">
                          {order.channel === 'ecommerce' ? 'E-commerce' : order.channel || '—'}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-foreground-900 font-medium whitespace-nowrap">${order.total.toLocaleString()}</td>
                      <td className="px-5 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.color} ${s.bg}`}>{s.label}</span>
                      </td>
                      <td className="px-5 py-3 text-foreground-500 whitespace-nowrap text-xs">
                        {order.createdAt ? new Date(order.createdAt).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' }) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {orders.length === 0 && (
              <div className="text-center py-10 text-foreground-500">
                <i className="ri-inbox-line text-3xl block mb-2"></i>
                <p className="text-sm">Sin pedidos recientes</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function CapitalItem({
  label,
  value,
  icon,
  tone,
  note,
}: {
  label: string;
  value: number;
  icon: string;
  tone: 'primary' | 'accent' | 'muted' | 'negative';
  note?: string;
}) {
  const toneCls = {
    primary: 'bg-primary-100 text-primary-700',
    accent: 'bg-accent-100 text-accent-700',
    muted: 'bg-background-100 text-foreground-500',
    negative: 'bg-secondary-100 text-secondary-900',
  }[tone];
  const valueCls =
    tone === 'negative'
      ? 'text-red-600'
      : tone === 'muted'
        ? 'text-foreground-500'
        : 'text-foreground-950';
  return (
    <div className="rounded-xl border border-background-200 p-3.5">
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${toneCls}`}>
          <i className={`${icon} text-sm`}></i>
        </div>
        <p className="text-xs text-foreground-500 leading-tight">{label}</p>
      </div>
      <p className={`text-base font-bold ${valueCls}`}>
        {value < 0 ? '-' : ''}${Math.abs(Math.round(value)).toLocaleString()}
      </p>
      {note && <p className="text-[11px] text-foreground-400 mt-0.5">{note}</p>}
    </div>
  );
}

function KpiCard({ label, value, icon, color }: { label: string; value: string; icon: string; color: string }) {
  return (
    <div className="bg-white rounded-xl border border-background-200 p-4 hover:border-background-300 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-foreground-500 truncate">{label}</p>
          <p className="text-lg font-bold text-foreground-950 mt-1 truncate">{value}</p>
        </div>
        <div className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center flex-shrink-0 ml-2`}>
          <i className={`${icon} text-white text-sm`}></i>
        </div>
      </div>
    </div>
  );
}